import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Ticket, CheckCircle2, Clock, AlertCircle, TrendingUp, Mail, ChevronLeft, ChevronRight, Plus, X, Copy, Share2, Megaphone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

const AdminCoupons = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [stats, setStats] = useState({ total: 0, used: 0, active: 0, expired: 0, conversion_percent: 0 });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCampaign, setShowCampaign] = useState(false);
  const [banner, setBanner] = useState({ enabled: false, coupon_code: '' });
  const [bannerSaving, setBannerSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/coupons/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/coupons?status=${statusFilter}&limit=${PAGE_SIZE}&skip=${skip}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (listRes.ok) {
        const data = await listRes.json();
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, skip]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/promo-banner`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setBanner(await res.json());
      } catch { /* noop */ }
    })();
  }, [token]);

  const updateBanner = async (patch) => {
    setBannerSaving(true);
    const next = { ...banner, ...patch };
    try {
      const res = await fetch(`${API_URL}/api/admin/promo-banner`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(next),
      });
      if (res.ok) setBanner(await res.json());
    } finally {
      setBannerSaving(false);
    }
  };

  useEffect(() => { setSkip(0); }, [statusFilter]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(i18n.language); } catch { return iso; }
  };

  const productName = (c) => {
    if (!c.product?.name) return c.product_id || '—';
    const n = c.product.name;
    return n[i18n.language] || n.sk || n.en || Object.values(n)[0] || c.product_id;
  };

  const statusBadge = (c) => {
    if (c.used) {
      return (
        <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded text-xs font-medium">
          <CheckCircle2 className="h-3 w-3" />
          {t('admin.coupons.used')}
        </span>
      );
    }
    if (c.is_expired) {
      return (
        <span className="inline-flex items-center gap-1 text-gray-600 bg-gray-100 px-2 py-0.5 rounded text-xs font-medium">
          <AlertCircle className="h-3 w-3" />
          {t('admin.coupons.expired')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-orange-700 bg-orange-50 px-2 py-0.5 rounded text-xs font-medium">
        <Clock className="h-3 w-3" />
        {t('admin.coupons.active')}
      </span>
    );
  };

  const pages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div className="max-w-7xl" data-testid="admin-coupons-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
            <Ticket className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.coupons.title')}</h1>
            <p className="text-sm text-gray-500">{t('admin.coupons.subtitle')}</p>
          </div>
        </div>
        <button
          onClick={() => setShowCampaign(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition shadow-sm"
          data-testid="new-campaign-btn"
        >
          <Plus className="h-4 w-4" />
          {t('admin.coupons.new_campaign')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label={t('admin.coupons.stat_total')} value={stats.total} />
        <StatCard label={t('admin.coupons.stat_active')} value={stats.active} color="orange" />
        <StatCard label={t('admin.coupons.stat_used')} value={stats.used} color="green" />
        <StatCard label={t('admin.coupons.stat_expired')} value={stats.expired} color="gray" />
        <StatCard
          label={t('admin.coupons.stat_conversion')}
          value={`${stats.conversion_percent}%`}
          icon={<TrendingUp className="h-4 w-4" />}
          color="blue"
        />
      </div>

      {/* Banner control */}
      <div
        className={`rounded-xl border-2 p-5 mb-4 transition ${
          banner.enabled
            ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-300'
            : 'bg-white border-gray-200'
        }`}
        data-testid="promo-banner-control"
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              banner.enabled ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-500'
            }`}>
              <Megaphone className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">{t('admin.coupons.banner.title')}</p>
              <p className="text-xs text-gray-500">
                {banner.enabled
                  ? t('admin.coupons.banner.enabled_hint')
                  : t('admin.coupons.banner.disabled_hint')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={banner.coupon_code || ''}
              onChange={(e) => updateBanner({ coupon_code: e.target.value })}
              disabled={bannerSaving || !banner.enabled}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="banner-code-select"
            >
              <option value="">{t('admin.coupons.banner.auto_select')}</option>
              {items
                .filter((c) => !c.email && !c.used && !c.is_expired)
                .map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} (−{c.discount_percent}%)
                  </option>
                ))}
            </select>
            <button
              type="button"
              role="switch"
              aria-checked={banner.enabled}
              onClick={() => updateBanner({ enabled: !banner.enabled })}
              disabled={bannerSaving}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition disabled:opacity-50 ${
                banner.enabled ? 'bg-orange-500' : 'bg-gray-300'
              }`}
              data-testid="banner-toggle"
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                  banner.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-gray-700">{t('admin.coupons.filter_label')}:</span>
        {['all', 'active', 'used', 'expired'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
              statusFilter === s
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            data-testid={`filter-${s}`}
          >
            {t(`admin.coupons.status_${s}`)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <Ticket className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">{t('admin.coupons.empty')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">{t('admin.coupons.code')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.coupons.discount')}</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">{t('admin.coupons.product')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.coupons.created')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.coupons.valid_until')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.coupons.status_label')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`coupon-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-gray-900 bg-gray-100 px-2 py-1 rounded text-xs">
                          {c.code}
                        </span>
                        {!c.email && !c.used && !c.is_expired && (
                          <ShareButton code={c.code} t={t} />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-orange-600">−{c.discount_percent}%</td>
                    <td className="px-4 py-3">
                      {c.email ? (
                        <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-gray-800 hover:text-orange-500">
                          <Mail className="h-3.5 w-3.5 text-gray-400" />
                          {c.email}
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {c.product ? (
                        <div>
                          <div className="text-gray-900 line-clamp-1">{productName(c)}</div>
                          {c.product.sku && (
                            <div className="text-xs text-gray-500 font-mono">{c.product.sku}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(c.created_at)}</td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(c.valid_until)}</td>
                    <td className="px-4 py-3">{statusBadge(c)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-500">
            {t('admin.coupons.showing', { from: skip + 1, to: Math.min(skip + PAGE_SIZE, total), total })}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              disabled={skip === 0}
              className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="pagination-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-gray-700">
              {currentPage} / {pages}
            </span>
            <button
              onClick={() => setSkip(skip + PAGE_SIZE)}
              disabled={skip + PAGE_SIZE >= total}
              className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="pagination-next"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {showCampaign && (
        <CampaignModal
          onClose={() => setShowCampaign(false)}
          onCreated={() => { setShowCampaign(false); fetchAll(); }}
          token={token}
          t={t}
        />
      )}
    </div>
  );
};

const CampaignModal = ({ onClose, onCreated, token, t }) => {
  const [audience, setAudience] = useState('stock_subscribers');
  const [discount, setDiscount] = useState(10);
  const [hours, setHours] = useState(48);
  const [sharedCode, setSharedCode] = useState('');
  const [customEmails, setCustomEmails] = useState('');
  const [subject, setSubject] = useState('');
  const [headline, setHeadline] = useState('');
  const [message, setMessage] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);
    const body = {
      audience,
      discount_percent: Number(discount),
      validity_hours: Number(hours),
      send_email: sendEmail,
      subject: subject || 'Sunify',
      headline: headline || t('admin.coupons.campaign.default_headline'),
      message,
      cta_label: ctaLabel || t('admin.coupons.campaign.default_cta'),
    };
    if (audience === 'shared_code' && sharedCode.trim()) {
      body.shared_code = sharedCode.trim().toUpperCase();
    }
    if (audience === 'custom_emails') {
      body.emails = customEmails
        .split(/[\s,;\n]+/)
        .map((x) => x.trim())
        .filter(Boolean);
    }
    try {
      const res = await fetch(`${API_URL}/api/admin/coupons/campaign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Failed');
      } else {
        const data = await res.json();
        setResult(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="campaign-modal"
      >
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold">{t('admin.coupons.campaign.title')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-modal">
            <X className="h-5 w-5" />
          </button>
        </div>

        {result ? (
          <div className="p-6 space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
                <div>
                  <p className="font-semibold text-green-800">
                    {t('admin.coupons.campaign.success', {
                      count: result.coupons_created,
                      emails: result.emails_sent,
                    })}
                  </p>
                  {result.sample_codes?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {result.sample_codes.map((c) => (
                        <span key={c} className="font-mono text-xs bg-gray-900 text-orange-300 px-2 py-1 rounded">{c}</span>
                      ))}
                      {result.coupons_created > result.sample_codes.length && (
                        <span className="text-xs text-gray-500 self-center">+{result.coupons_created - result.sample_codes.length}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onCreated}
              className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition"
              data-testid="modal-done"
            >
              {t('admin.coupons.campaign.done')}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            {/* Audience */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.coupons.campaign.audience')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {['stock_subscribers', 'registered_users', 'custom_emails', 'shared_code'].map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setAudience(a)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${
                      audience === a
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-orange-300'
                    }`}
                    data-testid={`audience-${a}`}
                  >
                    {t(`admin.coupons.campaign.audience_${a}`)}
                  </button>
                ))}
              </div>
            </div>

            {audience === 'custom_emails' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.coupons.campaign.emails_list')}
                </label>
                <textarea
                  rows={4}
                  value={customEmails}
                  onChange={(e) => setCustomEmails(e.target.value)}
                  placeholder="email1@example.com, email2@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="emails-textarea"
                />
              </div>
            )}

            {audience === 'shared_code' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.coupons.campaign.shared_code_label')}
                </label>
                <input
                  type="text"
                  value={sharedCode}
                  onChange={(e) => setSharedCode(e.target.value.toUpperCase())}
                  placeholder="BLACKFRIDAY15"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="shared-code-input"
                />
                <p className="text-xs text-gray-500 mt-1">{t('admin.coupons.campaign.shared_code_hint')}</p>
              </div>
            )}

            {/* Discount + validity */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.coupons.campaign.discount')} (%)
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="discount-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.coupons.campaign.hours')}
                </label>
                <input
                  type="number"
                  min="1"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="hours-input"
                />
              </div>
            </div>

            {/* Email content (only if sending) */}
            {audience !== 'shared_code' && (
              <div className="border-t pt-4 space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    data-testid="send-email-checkbox"
                  />
                  {t('admin.coupons.campaign.send_email')}
                </label>
                {sendEmail && (
                  <>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder={t('admin.coupons.campaign.subject')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      data-testid="subject-input"
                    />
                    <input
                      type="text"
                      value={headline}
                      onChange={(e) => setHeadline(e.target.value)}
                      placeholder={t('admin.coupons.campaign.headline')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      data-testid="headline-input"
                    />
                    <textarea
                      rows={3}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t('admin.coupons.campaign.message')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      data-testid="message-input"
                    />
                    <input
                      type="text"
                      value={ctaLabel}
                      onChange={(e) => setCtaLabel(e.target.value)}
                      placeholder={t('admin.coupons.campaign.cta_label')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      data-testid="cta-input"
                    />
                  </>
                )}
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="campaign-error">
                {error}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                data-testid="cancel-campaign"
              >
                {t('admin.coupons.campaign.cancel')}
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-2.5 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 disabled:bg-gray-300 transition"
                data-testid="submit-campaign"
              >
                {submitting ? '…' : t('admin.coupons.campaign.submit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon, color = 'default' }) => {
  const colorMap = {
    default: 'text-gray-900',
    orange: 'text-orange-600',
    green: 'text-green-600',
    gray: 'text-gray-500',
    blue: 'text-blue-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <p className={`text-3xl font-bold ${colorMap[color]}`}>{value}</p>
    </div>
  );
};

const ShareButton = ({ code, t }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/api/share/promo/${encodeURIComponent(code)}`;
  const ogImageUrl = `${window.location.origin}/api/og/promo/${encodeURIComponent(code)}.png`;

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* noop */
    }
  };

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="text-gray-400 hover:text-orange-500 transition"
        title={t('admin.coupons.share')}
        data-testid={`share-btn-${code}`}
      >
        <Share2 className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{t('admin.coupons.share_title', { code })}</h3>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <img src={ogImageUrl} alt="preview" className="w-full rounded-xl border border-gray-200 mb-4" />
            <div className="space-y-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('admin.coupons.share_url_label')}</label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={shareUrl}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-gray-50"
                    data-testid="share-url-input"
                  />
                  <button
                    onClick={() => copy(shareUrl)}
                    className="px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-1 text-sm"
                    data-testid="copy-share-url"
                  >
                    <Copy className="h-4 w-4" />
                    {copied ? t('admin.coupons.copied') : t('admin.coupons.copy')}
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500">{t('admin.coupons.share_hint')}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminCoupons;
